/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * profileModel
 * ------------
 * Pure readers for the athlete profile — the same numbers the UpVibe Profile
 * screen shows (ProfileViewModel in the upvibe project), computed here from
 * the synced `activity` rows so both surfaces agree:
 *   - lifetime distance, activity count and moving time
 *   - activity mix: count per type, most first, types never done left out
 *   - personal records: one per type in UpVibe's order — the longest-distance
 *     session (its own duration alongside), or the longest session for yoga;
 *     a type with no qualifying session gets a placeholder
 *
 * user_profile's own total_* columns are not used: the app writes them as 0.
 *
 * Every function takes normalized activities (feedModel.normalizeActivity).
 */

import { TYPE_ORDER, typeMeta } from '../feedModel';

export const KM_PER_MILE = 1.609344;

/** A record set within this many days gets the "New PR" highlight. */
export const NEW_PR_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** user_profile.preferred_unit → 'km' | 'mi' (KM when unset, as the app defaults it). */
export function unitOf(profile) {
  return String(profile?.preferred_unit || '').toUpperCase() === 'MILES' ? 'mi' : 'km';
}

export function kmIn(unit, km) {
  return unit === 'mi' ? km / KM_PER_MILE : km;
}

/** Yoga is the one type whose record is time, not distance (ActivityType.supportsDistance). */
export function supportsDistance(type) {
  return type !== 'yoga';
}

export function lifetimeStats(list) {
  return list.reduce((acc, a) => ({
    activities: acc.activities + 1,
    distanceKm: acc.distanceKm + (a.distanceKm || 0),
    movingSec: acc.movingSec + (a.durationSec || 0),
  }), { activities: 0, distanceKm: 0, movingSec: 0 });
}

/**
 * Lifetime distance as UpVibe shows it: converted to the user's unit, then
 * rounded once to a whole number with thousands separators (0.7 km → "1").
 */
export function formatLifetimeDistance(km, unit) {
  return Math.round(kmIn(unit, km)).toLocaleString('en-US');
}

/**
 * Lifetime moving time as UpVibe shows it: whole hours (floored) from one hour
 * up, otherwise whole minutes. Returns { value, unit } with unit 'HRS' or 'MIN'.
 */
export function formatMovingTime(sec) {
  const hours = Math.floor(sec / 3600);
  if (hours >= 1) return { value: hours.toLocaleString('en-US'), unit: 'HRS' };
  return { value: String(Math.floor(sec / 60)), unit: 'MIN' };
}

/** Personal-record distance as UpVibe shows it: always two decimals (9.80, 21.30). */
export function formatRecordDistance(km, unit) {
  return kmIn(unit, km).toFixed(2);
}

/** [{ type, label, icon, count }] for types done at least once, most first; ties keep app order. */
export function activityMix(list) {
  const counts = {};
  list.forEach((a) => { if (TYPE_ORDER.includes(a.type)) counts[a.type] = (counts[a.type] || 0) + 1; });
  return TYPE_ORDER
    .filter((t) => counts[t])
    .map((t) => ({ type: t, label: typeMeta(t).label, icon: typeMeta(t).icon, count: counts[t] }))
    .sort((x, y) => y.count - x.count);
}

/**
 * One entry per type, in UpVibe's order. `activity` is the record-holding
 * session, or null when there is none yet. On a tie the most recent session
 * wins (the list is newest first), matching the app.
 */
export function personalRecords(list, now = new Date()) {
  const holder = {};
  list.forEach((a) => {
    if (!TYPE_ORDER.includes(a.type)) return;
    const cur = holder[a.type];
    if (supportsDistance(a.type)) {
      if (!(a.distanceKm > 0)) return;
      if (!cur || a.distanceKm > cur.distanceKm) holder[a.type] = a;
    } else {
      if (!(a.durationSec > 0)) return;
      if (!cur || a.durationSec > cur.durationSec) holder[a.type] = a;
    }
  });

  const records = TYPE_ORDER.map((type) => ({
    type,
    label: typeMeta(type).label,
    icon: typeMeta(type).icon,
    byDistance: supportsDistance(type),
    activity: holder[type] || null,
    isNew: false,
  }));

  // Highlight the single most recently set record, if it is recent.
  const newest = records
    .filter((r) => r.activity && r.activity.startedAt)
    .sort((x, y) => y.activity.startedAt - x.activity.startedAt)[0];
  if (newest && now - newest.activity.startedAt <= NEW_PR_DAYS * DAY_MS) newest.isNew = true;
  return records;
}

/* ─────────────────────────── Personal details ─────────────────────────── */

/*
 * user_profile.gender is a plain string (max 20). A preset is stored as its
 * code; "Prefer to self-describe" stores the user's own words in the same
 * column, so any other non-blank value is a self-description. UpVibe reads and
 * writes it the same way (GenderOptions.java).
 */
export const GENDER_SELF_DESCRIBE = 'SELF_DESCRIBE';
export const GENDER_TEXT_MAX = 20;

const GENDER_LABEL = {
  MALE: 'Male', FEMALE: 'Female', NON_BINARY: 'Non-Binary', PREFER_NOT_TO_SAY: 'Prefer not to say',
};

export const GENDER_OPTIONS = [
  ...Object.entries(GENDER_LABEL).map(([value, label]) => ({ value, label })),
  { value: GENDER_SELF_DESCRIBE, label: 'Prefer to self-describe' },
];

/** The preset code a stored value holds (any case), or null. */
function genderPreset(value) {
  const upper = String(value || '').trim().toUpperCase();
  return Object.prototype.hasOwnProperty.call(GENDER_LABEL, upper) ? upper : null;
}

/** Trimmed, inner spaces collapsed, cut to the column length. */
function cleanGenderText(text) {
  return Array.from(String(text || '').trim().replace(/\s+/g, ' ')).slice(0, GENDER_TEXT_MAX).join('').trim();
}

/** A preset's label, a self-description as typed, or null when unset. */
export function genderLabel(value) {
  const preset = genderPreset(value);
  if (preset) return GENDER_LABEL[preset];
  return cleanGenderText(value) || null;
}

/** Which option a stored value selects ('' when unset). */
export function genderPickerKey(value) {
  if (!String(value || '').trim()) return '';
  return genderPreset(value) || GENDER_SELF_DESCRIBE;
}

/** The self-description a stored value holds, or ''. */
export function genderSelfDescription(value) {
  return genderPreset(value) ? '' : cleanGenderText(value);
}

/**
 * The value to store for a choice, or null for self-describe with no text.
 * Text that spells a preset is stored as that preset's code.
 */
export function genderToStore(key, text) {
  if (key !== GENDER_SELF_DESCRIBE) return key || null;
  const own = cleanGenderText(text);
  return own ? (genderPreset(own) || own) : null;
}

/** Whole years since a 'YYYY-MM-DD' date of birth, or null. */
export function ageFrom(dateOfBirth, now = new Date()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateOfBirth || ''));
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  let age = now.getFullYear() - y;
  if (now.getMonth() + 1 < mo || (now.getMonth() + 1 === mo && now.getDate() < d)) age -= 1;
  return age >= 0 ? age : null;
}

/**
 * An age → a date of birth, the way UpVibe onboarding stores it (today minus
 * `age` years). Only used when the age actually changes, so a real birth date
 * already on the row is not overwritten by an unchanged age.
 */
export function dobFromAge(age, now = new Date()) {
  const y = now.getFullYear() - age;
  const pad = (n) => String(n).padStart(2, '0');
  // 29 Feb in a non-leap year rolls to 1 Mar, which is still the right age.
  const d = new Date(y, now.getMonth(), now.getDate());
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/* ─────────────────────────── Mobile number ─────────────────────────── */

/*
 * Optional. Saved twice: split on the membership (country_code "+91",
 * mobile_number digits only — what checkout prefills from) and joined as
 * "+91 9876543210" in user_profile.mobile_number (max 20). The space keeps
 * the joined form splittable. Same rules as UpVibe's PhoneNumbers.java and
 * arcee's serializer.
 */
export const DEFAULT_COUNTRY_CODE = '+91';
export const MOBILE_STORED_MAX = 20;
export const MOBILE_HELPER =
  'Optional. We only use it for event updates and to send you discount coupons.';

const stripPhone = (v) => String(v || '').replace(/[\s\-()]/g, '');

/** "+91", or null when it is not "+" and 1-4 digits. A missing "+" is added. */
export function cleanCountryCode(code) {
  let c = String(code || '').trim();
  if (c && !c.startsWith('+')) c = `+${c}`;
  return /^\+\d{1,4}$/.test(c) ? c : null;
}

/**
 * Digits only (spaces, dashes, brackets dropped), or null when not exactly 10
 * digits — as at sign-up and event registration. arcee itself takes 4-15.
 */
export function cleanMobileNumber(number) {
  const n = stripPhone(number);
  return /^\d{10}$/.test(n) ? n : null;
}

export function isMobileEmpty(number) {
  return stripPhone(number) === '';
}

/** "+91 9876543210", or null when either half is invalid or it would not fit. */
export function joinMobile(code, number) {
  const c = cleanCountryCode(code);
  const n = cleanMobileNumber(number);
  if (!c || !n) return null;
  const joined = `${c} ${n}`;
  return joined.length <= MOBILE_STORED_MAX ? joined : null;
}

/** The dial code of a stored "+91 9876543210", or the default. */
export function mobileCodeOf(stored) {
  const s = String(stored || '').trim();
  const space = s.indexOf(' ');
  return (space > 0 && cleanCountryCode(s.slice(0, space))) || DEFAULT_COUNTRY_CODE;
}

/** The national number of a stored "+91 9876543210", or ''. */
export function mobileNumberOf(stored) {
  const s = String(stored || '').trim();
  const space = s.indexOf(' ');
  return cleanMobileNumber(space < 0 ? s : s.slice(space + 1)) || '';
}

/** The user_profile row → what the page shows. Missing values stay null. */
export function personalDetails(profile, now = new Date()) {
  const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
  const weight = num(profile?.weight_kg);
  const height = num(profile?.height_cm);
  return {
    gender: genderPreset(profile?.gender) || cleanGenderText(profile?.gender) || null,
    age: ageFrom(profile?.date_of_birth, now),
    weightKg: Number.isFinite(weight) ? weight : null,
    // 0 is not a height: a NOT NULL column added to old rows filled them with it.
    heightCm: Number.isFinite(height) && height > 0 ? height : null,
    mobile: String(profile?.mobile_number || '').trim() || null,
    city: (profile?.location_city || '').trim() || null,
    photoUrl: profile?.profile_photo_url || null,
  };
}

/**
 * True when the row lacks any detail every profile must have — gender,
 * weight, height, city — or there is no row at all. Same check as UpVibe's
 * UserProfileRepository.needsRequiredDetails.
 */
export function needsRequiredDetails(profile) {
  const d = personalDetails(profile);
  return !d.gender || !(d.weightKg > 0) || !(d.heightCm > 0) || !d.city;
}

/*
 * What can be TYPED, so a value that would not fit its column never reaches
 * the field: height_cm/weight_kg are NUMERIC(5,2) — 3 digits, then up to 2
 * after a "."; the mobile is digits only, at most 10; its code "+" then up to
 * 4 digits. Ranges are still checked on save. Same as UpVibe's InputLimits.
 */
export const MOBILE_MAX_DIGITS = 10;
export const fitsDecimal = (text) => /^\d{0,3}(\.\d{0,2})?$/.test(String(text));
export const fitsMobile = (text) => /^\d{0,10}$/.test(String(text));
export const fitsCountryCode = (text) => /^\+?\d{0,4}$/.test(String(text));

export const HEIGHT_MIN = 50;
export const HEIGHT_MAX = 250;
export const WEIGHT_MIN = 20;
export const WEIGHT_MAX = 300;
export const CITY_MAX = 255;

/**
 * The required-details fields (gender, height, weight, city, mobile) → error
 * messages by field. Shared by the edit sheet and the first sign-in screen.
 */
export function validateRequiredDetails(form) {
  const e = {};
  if (!form.gender) e.gender = 'Choose an option.';
  else if (form.gender === GENDER_SELF_DESCRIBE && !genderToStore(form.gender, form.genderText)) {
    e.genderText = 'Describe your gender, or choose another option.';
  }
  const h = Number(form.height);
  if (form.height === '' || !Number.isFinite(h) || h < HEIGHT_MIN || h > HEIGHT_MAX) {
    e.height = `Enter a height from ${HEIGHT_MIN} to ${HEIGHT_MAX} cm.`;
  }
  const w = Number(form.weight);
  if (form.weight === '' || !Number.isFinite(w) || w < WEIGHT_MIN || w > WEIGHT_MAX) {
    e.weight = `Enter a weight from ${WEIGHT_MIN} to ${WEIGHT_MAX} kg.`;
  }
  const city = form.city.trim();
  if (!city) e.city = 'Enter your city.';
  else if (city.length > CITY_MAX) e.city = `Keep the city under ${CITY_MAX} characters.`;
  if (!isMobileEmpty(form.mobileNumber)) {
    if (!cleanCountryCode(form.mobileCode)) e.mobileCode = 'Use + and 1–4 digits, e.g. +91.';
    else if (!joinMobile(form.mobileCode, form.mobileNumber)) e.mobileNumber = 'Enter a 10-digit mobile number.';
  }
  return e;
}

/** The mobile as stored ("+91 9876543210", or '' when left empty). Call after validating. */
export function mobileToStore(form) {
  return isMobileEmpty(form.mobileNumber) ? '' : (joinMobile(form.mobileCode, form.mobileNumber) || '');
}

/** The membership's pair for a stored mobile: { countryCode, mobileNumber }, both '' when empty. */
export function membershipMobile(stored) {
  return stored
    ? { countryCode: mobileCodeOf(stored), mobileNumber: mobileNumberOf(stored) }
    : { countryCode: '', mobileNumber: '' };
}

/**
 * Edit-form values → error messages by field ({} when valid). Ranges follow
 * user_profile's constraints (weight > 0, NUMERIC(5,2)) with sane human limits.
 */
export function validateProfileForm(form) {
  const e = validateRequiredDetails(form);
  if (!form.firstName.trim()) e.firstName = 'Enter your first name.';
  if (form.age !== '') {
    const age = Number(form.age);
    if (!Number.isInteger(age) || age < 10 || age > 100) e.age = 'Enter an age from 10 to 100.';
  }
  return e;
}

/**
 * The user_profile fields that changed, ready for table/save/record. Only
 * changed fields are sent: an update writes just the columns it is given.
 */
export function profileChanges(form, details, now = new Date()) {
  const out = {};
  const gender = genderToStore(form.gender, form.genderText);
  if (gender && gender !== details.gender) out.gender = gender;
  if (form.age !== '' && Number(form.age) !== details.age) out.date_of_birth = dobFromAge(Number(form.age), now);
  if (form.weight !== '' && Number(form.weight) !== details.weightKg) out.weight_kg = Number(Number(form.weight).toFixed(2));
  if (form.height !== '' && Number(form.height) !== details.heightCm) out.height_cm = Number(Number(form.height).toFixed(2));
  const mobile = mobileToStore(form);
  if (mobile !== (details.mobile || '')) out.mobile_number = mobile;
  const city = form.city.trim();
  if (city !== (details.city || '')) out.location_city = city || null;
  return out;
}

/** "Mar 2025" from a timestamp string, or null. */
export function memberSince(value) {
  if (!value) return null;
  const d = new Date(String(value).slice(0, 10) + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}
